/**
 * Typed mirror of the SCSS/CSS custom properties in `tokens/_root.scss`.
 *
 * The CSS custom properties are the single runtime source of truth — this file
 * exists so TypeScript code can *reference* a token by name (and get an autocompleted,
 * checked list) without hard-coding a hex value. Prefer `token('primary')`, which
 * emits `var(--ul-primary)` and therefore keeps following the design system, over
 * `HEX.primary`, which snapshots a value and will not.
 */

export const COLOR_VARS = {
  black: '--black',
  white: '--white',
  primary: '--ul-primary',
  secondary: '--ul-secondary',
  c3: '--ul-c3',
  c4: '--ul-c4',
  c5: '--ul-c5',
  ulBlack: '--ul-black',
  paragraph: '--ul-p',
  gray: '--ul-gray',
  gray2: '--ul-gray2',
  gray3: '--ul-gray3',
  /** "This is wrong" — a failed field and its message. Not a palette slot; see `_root.scss`. */
  danger: '--ul-danger',
  /** The rating star. Also not a palette slot, and for the same kind of reason. */
  star: '--ul-star',
  accent: '--ul-accent',
  gradient: '--ul-gradient',
  // The surface layer — see the comment block in `_root.scss`. Every one of these defaults
  // to a token above, so reading one in an app that has not overridden it gives that app's
  // existing colour rather than a dark one.
  surface: '--ul-surface',
  surface2: '--ul-surface-2',
  surface3: '--ul-surface-3',
  line: '--ul-line',
  onSurface: '--ul-on-surface',
  onSurfaceDim: '--ul-on-surface-dim',
  signal: '--ul-signal',
  signal2: '--ul-signal-2',
  onSignal: '--ul-on-signal',
} as const

export const FONT_VARS = {
  primary: '--font-primary',
  quicksand: '--font-quicksand',
  caveat: '--font-caveat',
  display: '--ul-display',
  mono: '--ul-mono',
  serif: '--ul-serif',
} as const

export type ColorToken = keyof typeof COLOR_VARS
export type FontToken = keyof typeof FONT_VARS

/** `token('primary')` -> `'var(--ul-primary)'` — stays live against the design system. */
export const token = (name: ColorToken): string => `var(${COLOR_VARS[name]})`
export const fontToken = (name: FontToken): string => `var(${FONT_VARS[name]})`

/**
 * Literal values, kept in sync with `tokens/_root.scss`. Only for contexts that
 * cannot accept a `var()` (canvas, meta theme-color, chart libraries, emails).
 * Reading a token at runtime instead is preferred where possible.
 */
export const HEX = {
  black: '#000',
  white: '#fff',
  primary: '#EAEEF3',
  secondary: '#2F5D8C',
  c3: '#1C3D5C',
  c4: '#DCE4EC',
  c5: '#24303D',
  ulBlack: '#16202B',
  paragraph: '#2A3745',
  gray: '#2A3745',
  gray2: '#8E97A1',
  gray3: '#F6F8FA',
  danger: '#B3261E',
  star: '#F5A524',
  // `--ul-accent` resolves to `--ul-secondary` in the system's own palette, so the literal
  // here is that value. An app that overrides the token has its own value at runtime —
  // which is the usual reason to prefer `token('accent')` over `HEX.accent`.
  accent: '#2F5D8C',
  // The surface layer's literals are the values its defaults resolve to in the system's own
  // palette — i.e. the light mode. The garage overrides the tokens, so its runtime values
  // are not these, which is the usual reason to prefer `token()` or `readToken()` here.
  surface: '#fff',
  surface2: '#F6F8FA',
  surface3: '#DCE4EC',
  line: '#DCE4EC',
  onSurface: '#16202B',
  onSurfaceDim: '#2A3745',
  signal: '#2F5D8C',
  signal2: '#2F5D8C',
  onSignal: '#EAEEF3',
} as const satisfies Record<Exclude<ColorToken, 'gradient'>, string>

/** Reads a token's computed value from the document — always current. */
export const readToken = (name: ColorToken, el: Element = document.documentElement): string =>
  getComputedStyle(el).getPropertyValue(COLOR_VARS[name]).trim()

/**
 * The site's responsive breakpoints, mirroring `tokens/_breakpoints.scss`.
 * All are `max-width` — the design is desktop-first. Values must not drift from
 * the SCSS mixins; `useBreakpoint()` consumes these.
 */
export const BREAKPOINTS = {
  xxxxl: 1880,
  xxxl: 1799,
  xxl: 1599,
  xl: 1399,
  lg: 1199,
  md: 991,
  sm: 767,
  xs: 575,
  xxs: 479,
} as const

export type Breakpoint = keyof typeof BREAKPOINTS
